import React, { useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  
  // Handle external links with proper attributes
  const handleExternalLink = useCallback((e, url) => {
    e.preventDefault();
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);
  
  const footerLinks = [
    {
      title: 'Events',
      links: [
        { 
          name: 'Upcoming Conferences', 
          url: '/events',
          external: false,
          description: 'View all upcoming conferences and events'
        },
        { 
          name: 'Past Events', 
          url: '/events/past',
          external: false,
          description: 'Browse our past conferences and events'
        },
        { 
          name: 'Submit an Event', 
          url: '/submit-event',
          external: false,
          description: 'Submit your event to be featured'
        },
      ],
    },
    {
      title: 'Organizations',
      links: [
        { 
          name: 'UPCI', 
          url: 'https://upci.org/',
          external: true,
          fullName: 'United Pentecostal Church International',
          description: 'Official UPCI website'
        },
        { 
          name: 'ALJC', 
          url: 'https://aljc.org/',
          external: true,
          fullName: 'Assemblies of the Lord Jesus Christ',
          description: 'Official ALJC website'
        },
        { 
          name: 'WPF', 
          url: 'https://www.gowpf.org/',
          external: true,
          fullName: 'Worldwide Pentecostal Fellowship',
          description: 'Official WPF website'
        },
        { 
          name: 'AFF', 
          url: 'https://www.apostolicfaithfellowship.org/',
          external: true,
          fullName: 'Apostolic Faith Fellowship',
          description: 'Official AFF website'
        },
      ],
    },
    {
      title: 'Resources',
      links: [
        { 
          name: 'Venue Information', 
          url: '/venue',
          external: false,
          description: 'Details about our event venues'
        },
        { 
          name: 'Travel & Accommodation', 
          url: '/travel',
          external: false,
          description: 'Travel and hotel information'
        },
        { 
          name: 'FAQ', 
          url: '/faq',
          external: false,
          description: 'Frequently asked questions'
        },
        { 
          name: 'Contact Us', 
          url: '/contact',
          external: false,
          description: 'Get help or ask questions'
        },
        { 
          name: 'Support Us', 
          url: '/support',
          external: false,
          description: 'Help keep this resource free'
        },
      ],
    },
    {
      title: 'Legal',
      links: [
        { 
          name: 'Privacy Policy', 
          url: '/privacy',
          external: false,
          description: 'Our privacy policy'
        },
        { 
          name: 'Terms of Service', 
          url: '/terms',
          external: false,
          description: 'Terms and conditions'
        },
        { 
          name: 'Code of Conduct', 
          url: '/code-of-conduct',
          external: false,
          description: 'Event code of conduct'
        },
      ],
    },
  ];

  const socialLinks = [
    { name: 'Facebook', icon: 'M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z', url: '#' },
    { name: 'Twitter', icon: 'M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84', url: '#' },
    { name: 'Instagram', icon: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z', url: '#' },
    { name: 'YouTube', icon: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z', url: '#' },
  ];

  return (
    <footer className="bg-gray-900 text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 mb-12">
          <div className="col-span-2">
            <h3 className="text-2xl font-bold mb-1">ACP Conference Planner</h3>
            <p className="text-sm text-gray-500 mb-4">A Product of Resurgence Technology Group, LLC</p>
            <p className="text-gray-400 mb-6">
              Your complete guide to Apostolic conferences and events. Discover schedules, connect with speakers, and plan your journey with ease.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((item) => (
                <a
                  key={item.name}
                  href={item.url}
                  onClick={(e) => handleExternalLink(e, item.url)}
                  aria-label={`${item.name} (opens in new tab)`}
                  title={item.name}
                >
                  <span className="sr-only">{item.name}</span>
                  <svg 
                    className="h-6 w-6 transition-transform duration-200 group-hover:scale-110"
                    fill="currentColor" 
                    viewBox="0 0 24 24" 
                    aria-hidden="true"
                    role="img"
                    tabIndex="-1"
                  >
                    <path 
                      fillRule="evenodd" 
                      d={item.icon} 
                      clipRule="evenodd"
                    />
                  </svg>
                </a>
              ))}
            </div>
          </div>
          {footerLinks.map((section) => (
            <div key={section.title}>
              <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
                {section.title}
              </h4>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.name} className="group relative">
                    {link.external ? (
                      <a
                        href={link.url}
                        onClick={(e) => handleExternalLink(e, link.url)}
                        className="text-gray-400 hover:text-white transition-colors duration-200 inline-flex items-center"
                        aria-label={`${link.name} (opens in new tab)`}
                        title={link.description || link.fullName || link.name}
                      >
                        {link.name}
                        <svg className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ) : link.name === 'FAQ' ? (
                      <a
                        href={link.url}
                        className="text-gray-400 hover:text-white transition-colors duration-200 inline-flex items-center"
                        title={link.description || link.name}
                      >
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        to={link.url}
                        className="text-gray-400 hover:text-white transition-colors duration-200 inline-flex items-center"
                        title={link.description || link.name}
                      >
                        {link.name}
                      </Link>
                    )}
                    {link.fullName && (
                      <span className="absolute left-0 -top-6 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                        {link.fullName}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-center md:text-left">
              <p className="text-gray-400 text-sm">
                &copy; {currentYear} Resurgence Technology Group, LLC. All rights reserved.
              </p>
              <p className="text-gray-500 text-sm mt-1">
                ACP Conference Planner is a product of Resurgence Technology Group, LLC.
              </p>
            </div>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm">
                Terms of Service
              </Link>
              <Link to="/code-of-conduct" className="text-gray-400 hover:text-white text-sm">
                Code of Conduct
              </Link>
            </div>
          </div>
          <div className="mt-4 text-center md:text-left space-y-2">
            <p className="text-xs text-gray-500">
              ACP Conference Planner is an independent conference-management platform and is not affiliated with, endorsed by, or sponsored by any church, denomination, fellowship, ministry, or religious organization unless expressly stated.
            </p>
            <p className="text-xs text-gray-500">
              Conference names, organization names, logos, service marks, and trademarks remain the property of their respective owners and are used solely for identification and informational purposes. Their inclusion does not imply endorsement of or affiliation with ACP Conference Planner or Resurgence Technology Group, LLC.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
